import algoliasearch from 'algoliasearch/lite';

import getRelatedFilters from '@core/blocks/edu-match/utils/getRelatedFilters';
import sortHits from '@core/blocks/edu-match/utils/sortHits';
import type { AlgoliaCredentials, Hit } from '@core/ts/algolia';
import getLdsStartDate from '@core/utils/getLdsStartDate';
import { isUnderGraduate } from '@core/utils/isUnderGraduate';
import getFilters from '@core/utils/spotlight/getFilters';

import { getLeadQualifications } from '../leadDelivery';
import { mobiusGetAlgoliaCredentials } from '../mobius';

export const ALGOLIA_BASE_INDEX_NAME = import.meta.env.VITE_ALGOLIA_BASE_INDEX;
export const ALGOLIA_RECOMMENDED_INDEX_NAME = `${ALGOLIA_BASE_INDEX_NAME}_recommended`;
export const ALGOLIA_RELATED_INDEX_NAME = `${ALGOLIA_BASE_INDEX_NAME}_related`;

type GetHitsParams = {
  search?: string;
  filters?: string;
  facetFilters?: string[];
  hitsPerPage?: number;
  inputs: {
    [key: string]: { value: unknown };
  };
  applyQualifications?: boolean;
  indexName?: string;
  forceBrandedSchoolIds?: null | number[];
};

type GetRecommenderResultsParams = {
  dcs: any;
  configFilters: any;
  inputs: {
    [key: string]: { value: unknown };
  };
  hitsPerPage: number;
  indexName: string;
  forceBrandedSchoolIds?: null | number[];
};

type GetExactResultsParams = {
  dcs: any;
  configFilters: any;
  inputs: {
    [key: string]: { value: unknown };
  };
  hitsPerPage?: number;
  forceBrandedSchoolIds?: null | number[];
};

type GetRelatedResultsParams = {
  degrees: string[];
  subjects: string[];
  inputs: {
    [key: string]: { value: unknown };
  };
  configFilters: any;
  hitsPerPage?: number;
  forceBrandedSchoolIds?: null | number[];
};

export const getSearchClient = async (
  inputs,
  applyQualifications = true,
  forceBrandedSchoolIds: null | number[] = null
) => {
  const qualifications =
    applyQualifications && inputs?.firstName?.value && inputs?.lastName?.value && inputs?.email?.value
      ? await getLeadQualifications(inputs)
      : undefined;

  const mobiusOptions = {
    criteriaOverride: {
      unqualified: qualifications?.unqualified?.map(({ id }) => id),
      qualified: qualifications?.qualified?.map(({ id }) => id),
      startDate: getLdsStartDate(inputs?.startTime?.value),
      forceAudience: forceBrandedSchoolIds
        ? {
            audience: 'branded',
            schoolIds: forceBrandedSchoolIds,
          }
        : undefined,
    },
    zip: inputs?.zip?.value,
  };

  const credentials: AlgoliaCredentials = await mobiusGetAlgoliaCredentials(mobiusOptions);

  return algoliasearch(import.meta.env.VITE_ALGOLIA_APP_ID, credentials?.key);
};

const AlgoliaServiceV2 = {
  async getHits({
    search = '',
    filters = '',
    facetFilters = [],
    hitsPerPage = 51,
    inputs,
    applyQualifications = true,
    indexName = ALGOLIA_BASE_INDEX_NAME,
    forceBrandedSchoolIds = null,
  }: GetHitsParams) {
    const algoliaClient = await getSearchClient(inputs, applyQualifications, forceBrandedSchoolIds);

    const algoliaIndex = algoliaClient.initIndex(indexName);

    return algoliaIndex.search(search, {
      filters,
      facetFilters,
      hitsPerPage,
    });
  },

  async getRecommenderResults({
    dcs,
    inputs,
    configFilters,
    hitsPerPage,
    indexName,
    forceBrandedSchoolIds = null,
  }: GetRecommenderResultsParams) {
    const {
      dcsDegrees: [degree],
    } = dcs;

    if (!isUnderGraduate(degree)) {
      const twoUFilters = getFilters({ dcs, configFilters, additionalFilters: "program.providerName:'TwoU'" });
      const nonTwoUFilters = getFilters({ dcs, configFilters, additionalFilters: "NOT program.providerName:'TwoU'" });

      const TwoU = await AlgoliaServiceV2.getHits({
        inputs,
        filters: twoUFilters,
        hitsPerPage: 1,
        indexName: ALGOLIA_RECOMMENDED_INDEX_NAME,
        forceBrandedSchoolIds,
      });
      const NonTwoU = await AlgoliaServiceV2.getHits({
        inputs,
        filters: nonTwoUFilters,
        hitsPerPage,
        indexName,
        forceBrandedSchoolIds,
      });
      return [...TwoU.hits, ...NonTwoU.hits];
    }

    const filters = getFilters({ dcs, configFilters });

    const recommended = await AlgoliaServiceV2.getHits({
      inputs,
      filters,
      hitsPerPage,
      indexName: ALGOLIA_RECOMMENDED_INDEX_NAME,
      forceBrandedSchoolIds,
    });

    return sortHits(recommended.hits as Hit[], inputs?.zip?.value as string);
  },

  async getExactResults({
    dcs,
    inputs,
    configFilters,
    hitsPerPage = 51,
    forceBrandedSchoolIds = null,
  }: GetExactResultsParams) {
    const filters = getFilters({ dcs, configFilters });
    const algoliaHits = await AlgoliaServiceV2.getHits({ inputs, filters, hitsPerPage, forceBrandedSchoolIds });
    return algoliaHits.hits;
  },

  async getRelatedResults({
    degrees,
    subjects,
    inputs,
    configFilters,
    hitsPerPage = 12,
    forceBrandedSchoolIds = null,
  }: GetRelatedResultsParams) {
    const filters = getRelatedFilters({ degrees, subjects, configFilters });
    const algoliaHits = await AlgoliaServiceV2.getHits({
      inputs,
      filters,
      hitsPerPage,
      indexName: `${ALGOLIA_BASE_INDEX_NAME}_related`,
      forceBrandedSchoolIds,
    });
    return algoliaHits.hits;
  },
};

export default AlgoliaServiceV2;
